import { computed } from 'vue';
import { useRuntimeConfig } from '#imports';
import useAuthStore, { type NavItem } from '~/stores/auth';
import { LmsRouteNames } from '~/types/types';
import type { NuxtLinkProps } from 'nuxt/app';

const NAV_ROUTE_MAP: Record<string, NuxtLinkProps['to'] | null> = {
   home: { name: LmsRouteNames.home },
   play_learn: { name: LmsRouteNames.lmsPlayLearn },
   e_learning: null,
   levels: { name: LmsRouteNames.byLevel },
   topics_areas: { name: LmsRouteNames.courseCategories },
   all_content: { name: LmsRouteNames.allContent },
   contributors: { name: LmsRouteNames.contributors },
   blog: { name: LmsRouteNames.blogHome },
   access_options: null,
   premium_packages: '/lms/premium',
   premium_subscriptions: '/lms/premium-subscriptions',
   site_wide_subscription: { name: LmsRouteNames.subscribe },
   tournaments: { name: LmsRouteNames.tournaments },
   chukkers: { name: LmsRouteNames.chukkers },
   video_gallery: { name: LmsRouteNames.video },
   image_gallery: { name: LmsRouteNames.gallery },
   memberships: { name: LmsRouteNames.memberships },
   club_listings: { name: LmsRouteNames.clubListings },
   leaderboard: { name: LmsRouteNames.leaderboard },
   members_portal: 'https://members.uspolo.org/',
};

const EXTERNAL_NAV_KEYS = new Set(['members_portal']);

/**
 * Where brand-authored custom pages live. The API sends a slug and nothing else, so the
 * URL space is ours; the prefix keeps a brand-chosen slug from ever colliding with a
 * static route such as /lms/tournaments.
 */
export const CUSTOM_PAGE_PATH_PREFIX = '/lms/pages';

export function customPagePath(slug: string): string {
   return `${CUSTOM_PAGE_PATH_PREFIX}/${slug}`;
}

export function useLmsNavigation() {
   const authStore = useAuthStore();
   // Declared up here rather than beside getNavRoute. That function is hoisted, so it can
   // be called from anything above it - an immediate watcher, say - and a const declared
   // further down would still be in its temporal dead zone at that point.
   const config = useRuntimeConfig();


   const settingsLoaded = computed(
      () => !authStore.isFetchingUserProfile && authStore.user?.user?.email !== '',
   );

   const navItems = computed<NavItem[]>(
      () => authStore?.user?.site_settings?.nav_items ?? [],
   );

   function findNavItem(key: string): NavItem | undefined {
      for (const item of navItems.value) {
         if (item.key === key) {
            return item;
         }
         if (item.children) {
            const child = item.children.find(c => c.key === key);
            if (child) {
               return child;
            }
         }
      }
      return undefined;
   }

   function isNavItemVisible(key: string): boolean {
      const item = findNavItem(key);
      if (!item) {
         return false;
      }
      return item.is_visible;
   }

   function getNavItemLabel(key: string, fallback: string): string {
      const item = findNavItem(key);
      return item?.label || fallback;
   }

   function isExternalLink(key: string): boolean {
      return EXTERNAL_NAV_KEYS.has(key);
   }

   function getNavRoute(key: string): NuxtLinkProps['to'] | null {
      // Polo Ireland's LMS "Home" links to the public homepage
      if (key === 'home' && config.public.portalBrand === 'poloireland') {
         return '/';
      }
      return NAV_ROUTE_MAP[key] ?? null;
   }

   /**
    * Resolve a nav item to a link target. Custom pages carry their own slug and are not
    * in NAV_ROUTE_MAP, so they are resolved from the slug; everything else falls back to
    * the built-in key map. Prefer this over getNavRoute wherever a whole item is in hand.
    */
   function getNavItemRoute(item: NavItem): NuxtLinkProps['to'] | null {
      if (item.type !== 'custom_page') {
         return getNavRoute(item.key);
      }

      // A custom page without a slug has nowhere to point. The nav template drops items
      // with a null target, which is the right outcome for a malformed payload.
      if (!item.slug) {
         return null;
      }

      return customPagePath(item.slug);
   }

   const visibleNavItems = computed<NavItem[]>(() => {
      if (!settingsLoaded.value) {
         return [];
      }
      const items = navItems.value
         .filter(item => item.is_visible)
         .map(item => {
            if (!item.children?.length) {
               return item;
            }
            return {
               ...item,
               children: item.children.filter(child => child.is_visible),
            };
         });

      if (config.public.portalBrand === 'poloireland') {
         items.push({
            key: 'club_listings',
            label: 'Club Listings',
            position: items.length + 1,
            is_visible: true,
         });
      }

      return items;
   });

   return {
      settingsLoaded,
      navItems,
      visibleNavItems,
      findNavItem,
      isNavItemVisible,
      getNavItemLabel,
      isExternalLink,
      getNavRoute,
      getNavItemRoute,
      NAV_ROUTE_MAP,
   };
}
